/**
 * @fileoverview Hook integrado para dados históricos
 * @module hooks/useDadosHistoricosIntegrado
 * 
 * @description
 * Hook que retorna dados históricos priorizando dados editados do DREStore
 * sobre dados master do HistoricoParser.
 * 
 * @version 1.0.0
 * @date 2025-10-17
 */

import { useMemo } from 'react';
import { useDREIntegration } from './useDREIntegration';
import { 
  useDadosHistoricos, 
  type DadosHistoricosCompletos,
  type HistoricoEmpresa,
  type DREHistorico,
  type MetricasHistoricas
} from '../parsers/HistoricoParser';
import type { DREYear } from '../types/dre';

/**
 * Converte DREYear (do DREStore) para DREHistorico (do HistoricoParser)
 */
function convertDREYearToHistorico(year: DREYear): DREHistorico {
  return {
    ano: year.year,
    receitaBruta: year.receitaBruta,
    impostosVendas: year.impostosVendas,
    receitaLiquida: year.receitaLiquida,
    custosDiretos: year.custosDirectos, // Note: 'custosDirectos' -> 'custosDiretos'
    lucroBruto: year.lucroBruto,
    despesasOperacionais: year.despesasOperacionais,
    ebitda: year.ebitda,
    depreciacao: year.depreciacao,
    ebit: year.ebit,
    impostosLucro: year.impostosLucro,
    lucroLiquido: year.lucroLiquido,
    margens: {
      bruta: year.margemBruta,
      ebitda: year.margemEbitda,
      ebit: year.margemEbit,
      liquida: year.margemLiquida,
    },
  };
}

/**
 * Calcula CAGR (Compound Annual Growth Rate)
 * 
 * @returns CAGR ou NaN se inválido (valores negativos/zero)
 */
function calcularCAGR(valorInicial: number, valorFinal: number, anos: number): number {
  // Retorna NaN para casos inválidos (será tratado na UI)
  if (valorInicial <= 0 || valorFinal <= 0 || anos <= 0) {
    return NaN;
  }
  
  return Math.pow(valorFinal / valorInicial, 1 / anos) - 1;
}

/**
 * Calcula média de array
 */
function calcularMedia(valores: number[]): number {
  if (valores.length === 0) return 0;
  return valores.reduce((sum, val) => sum + val, 0) / valores.length;
}

/**
 * Calcula desvio padrão
 */
function calcularDesvioPadrao(valores: number[]): number {
  if (valores.length === 0) return 0;
  const media = calcularMedia(valores);
  const variancia = valores.reduce((sum, val) => sum + Math.pow(val - media, 2), 0) / valores.length;
  return Math.sqrt(variancia);
}

/**
 * Calcula métricas históricas de uma lista de anos
 */
function calcularMetricas(anos: DREHistorico[]): MetricasHistoricas {
  const anosOrdenados = anos.sort((a, b) => a.ano - b.ano);
  const anoInicial = anosOrdenados[0].ano;
  const anoFinal = anosOrdenados[anosOrdenados.length - 1].ano;
  const numAnos = anoFinal - anoInicial;

  const receitas = anosOrdenados.map(ano => ano.receitaBruta);
  const ebitdas = anosOrdenados.map(ano => ano.ebitda);
  const lucros = anosOrdenados.map(ano => ano.lucroLiquido);
  const margensEbitda = anosOrdenados.map(ano => ano.margens.ebitda);
  const margensBruta = anosOrdenados.map(ano => ano.margens.bruta);
  const margensLiquida = anosOrdenados.map(ano => ano.margens.liquida);

  return {
    cagr: {
      receita: calcularCAGR(receitas[0], receitas[receitas.length - 1], numAnos),
      ebitda: calcularCAGR(ebitdas[0], ebitdas[ebitdas.length - 1], numAnos),
      lucroLiquido: calcularCAGR(lucros[0], lucros[lucros.length - 1], numAnos),
    },
    medias: {
      margemBruta: calcularMedia(margensBruta),
      margemEbitda: calcularMedia(margensEbitda),
      margemLiquida: calcularMedia(margensLiquida),
      receita: calcularMedia(receitas),
    },
    volatilidade: {
      receita: calcularDesvioPadrao(receitas),
      margemEbitda: calcularDesvioPadrao(margensEbitda),
    },
    periodoInicio: anoInicial,
    periodoFim: anoFinal,
    anosAnalise: anosOrdenados.length,
  };
}

/**
 * Hook que retorna dados históricos integrados
 * Prioriza dados editados do DREStore sobre dados master
 * 
 * @returns {DadosHistoricosCompletos} Dados históricos completos
 * 
 * @example
 * ```tsx
 * function HistoricoPage() {
 *   const dados = useDadosHistoricosIntegrado();
 *   
 *   // dados.setima.anos[2024] reflete edições se existirem
 *   return <Table data={dados.setima.anos} />;
 * }
 * ```
 */
export function useDadosHistoricosIntegrado(): DadosHistoricosCompletos {
  const { dreData, hasEdits } = useDREIntegration();
  const dadosMaster = useDadosHistoricos();

  const dadosIntegrados = useMemo<DadosHistoricosCompletos>(() => {
    // Se não há edições, retorna dados master diretamente
    if (!hasEdits || !dreData) {
      return dadosMaster;
    }

    // Se há edições, converte dreData para formato HistoricoParser
    
    // Converter Sétima
    const setimaYears = dreData.setima.years.map(convertDREYearToHistorico);
    const setimaAnos: { [ano: number]: DREHistorico } = {};
    setimaYears.forEach(year => {
      setimaAnos[year.ano] = year;
    });
    
    const setimaIntegrada: HistoricoEmpresa = {
      empresa: 'Sétima',
      anos: setimaAnos,
      metricas: calcularMetricas(setimaYears),
    };

    // Converter 555 Studios
    const studio555Years = dreData.studio555.years.map(convertDREYearToHistorico);
    const studio555Anos: { [ano: number]: DREHistorico } = {};
    studio555Years.forEach(year => {
      studio555Anos[year.ano] = year;
    });
    
    const studio555Integrada: HistoricoEmpresa = {
      empresa: '555 Studios',
      anos: studio555Anos,
      metricas: calcularMetricas(studio555Years),
    };

    // Calcular consolidado
    const todosAnos = new Set([
      ...Object.keys(setimaAnos).map(Number),
      ...Object.keys(studio555Anos).map(Number),
    ]);

    const consolidadoAnos: {
      [ano: number]: {
        receitaTotal: number;
        ebitdaTotal: number;
        margemEbitda: number;
      };
    } = {};

    todosAnos.forEach(ano => {
      const receitaSetima = setimaAnos[ano]?.receitaBruta || 0;
      const ebitdaSetima = setimaAnos[ano]?.ebitda || 0;
      const receita555 = studio555Anos[ano]?.receitaBruta || 0;
      const ebitda555 = studio555Anos[ano]?.ebitda || 0;

      const receitaTotal = receitaSetima + receita555;
      const ebitdaTotal = ebitdaSetima + ebitda555;
      const margemEbitda = receitaTotal > 0 ? (ebitdaTotal / receitaTotal) * 100 : 0;

      consolidadoAnos[ano] = {
        receitaTotal,
        ebitdaTotal,
        margemEbitda,
      };
    });

    // Calcular métricas consolidadas
    const anosCompletos = Array.from(todosAnos).sort();
    const receitaInicial = consolidadoAnos[anosCompletos[0]].receitaTotal;
    const receitaFinal = consolidadoAnos[anosCompletos[anosCompletos.length - 1]].receitaTotal;
    const ebitdaInicial = consolidadoAnos[anosCompletos[0]].ebitdaTotal;
    const ebitdaFinal = consolidadoAnos[anosCompletos[anosCompletos.length - 1]].ebitdaTotal;

    const metricasConsolidado: MetricasHistoricas = {
      cagr: {
        receita: calcularCAGR(receitaInicial, receitaFinal, anosCompletos.length - 1),
        ebitda: calcularCAGR(ebitdaInicial, ebitdaFinal, anosCompletos.length - 1),
        lucroLiquido: 0,
      },
      medias: {
        margemBruta: 0,
        margemEbitda: calcularMedia(anosCompletos.map(ano => consolidadoAnos[ano].margemEbitda)),
        margemLiquida: 0,
        receita: calcularMedia(anosCompletos.map(ano => consolidadoAnos[ano].receitaTotal)),
      },
      volatilidade: {
        receita: calcularDesvioPadrao(anosCompletos.map(ano => consolidadoAnos[ano].receitaTotal)),
        margemEbitda: calcularDesvioPadrao(anosCompletos.map(ano => consolidadoAnos[ano].margemEbitda)),
      },
      periodoInicio: anosCompletos[0],
      periodoFim: anosCompletos[anosCompletos.length - 1],
      anosAnalise: anosCompletos.length,
    };

    return {
      setima: setimaIntegrada,
      studio555: studio555Integrada,
      consolidado: {
        anos: consolidadoAnos,
        metricas: metricasConsolidado,
      },
    };
  }, [dreData, hasEdits, dadosMaster]);

  return dadosIntegrados;
}
